import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';

const messages = defineMessages({
  recycleBin: {
    id: 'autoCuration.pdfSplitEditor.recycleBin',
    defaultMessage: 'Recycle Bin',
  },
  restore: {
    id: 'autoCuration.pdfSplitEditor.restorePages',
    defaultMessage: 'Restore {count} {count, plural, one {page} other {pages}}',
  },
  page: {
    id: 'autoCuration.pdfSplitEditor.pageNumber',
    defaultMessage: 'Page {num}',
  },
  noDeletedPages: {
    id: 'autoCuration.pdfSplitEditor.noDeletedPages',
    defaultMessage: 'No deleted page',
  },
});

interface RecycleBinProps {
  removedPages: number[];
  onRestorePage: (pages: number[]) => void;
}

export const RecycleBin = ({
  onRestorePage,
  removedPages,
}: RecycleBinProps) => {
  const { formatMessage } = useIntl();
  const [binAnchorEl, setBinAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRemovedPages, setSelectedRemovedPages] = useState<number[]>(
    []
  );

  const handleOpenBin = (event: React.MouseEvent<HTMLElement>) => {
    setBinAnchorEl(event.currentTarget);
  };

  const handleCloseBin = () => {
    setBinAnchorEl(null);
    setSelectedRemovedPages([]);
  };

  const handleTogglePageSelection = (page: number) => {
    setSelectedRemovedPages((prev) =>
      prev.includes(page) ? prev.filter((p) => p !== page) : [...prev, page]
    );
  };

  const handleRestoreSelected = () => {
    if (selectedRemovedPages.length > 0) {
      onRestorePage(selectedRemovedPages);
    }
    handleCloseBin();
  };

  return (
    <>
      <IconButton
        size="small"
        color="primary"
        onClick={handleOpenBin}
        title={formatMessage(messages.recycleBin)}
        sx={{
          bgcolor: 'white',
          border: '1.5px solid',
          borderColor: 'primary.main',
          borderRadius: '6px',
          width: 28,
          height: 28,
          transition: 'all 0.2s',
          '&:hover': {
            bgcolor: 'primary.main',
            color: 'white',
            transform: 'scale(1.08)',
          },
        }}
      >
        <Icon name="trash-arrow-up" type="fas" style={{ fontSize: 16 }} />
      </IconButton>
      <Menu
        anchorEl={binAnchorEl}
        open={Boolean(binAnchorEl)}
        onClose={handleCloseBin}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 240, maxHeight: 300 } } }}
      >
        {removedPages.length === 0 ? (
          <MenuItem disabled>{formatMessage(messages.noDeletedPages)}</MenuItem>
        ) : (
          <>
            {removedPages.map((page) => (
              <MenuItem
                key={page}
                onClick={() => handleTogglePageSelection(page)}
                sx={{ py: 0.75 }}
              >
                <ListItemIcon>
                  <Checkbox
                    checked={selectedRemovedPages.includes(page)}
                    edge="start"
                    size="small"
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText
                  primary={formatMessage(messages.page, {
                    num: page,
                  })}
                />
              </MenuItem>
            ))}
            <Divider />
            <MenuItem
              onClick={handleRestoreSelected}
              disabled={selectedRemovedPages.length === 0}
            >
              <ListItemText
                primary={formatMessage(messages.restore, {
                  count: selectedRemovedPages.length,
                })}
                sx={{ color: 'primary.main', fontWeight: 600 }}
              />
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};
